import React from 'react';
import { Box, Chip, IconButton, InputAdornment, List, ListItem, TextField, Typography } from '@mui/material';
import { IBuildingArea } from '../../models';
import { ArrowForward } from '@mui/icons-material';
import { useDeleteBuildingAreaMutation } from '../../store/apis';
import { useAppSelector } from '../../store/store.hook';
import { selectCurrentUserInfo } from '../../store/slices';
import { useErrorToast } from '../../hooks/toasts/error-toast.hook';
import { toast } from 'react-toastify';

interface BuildingAreasListProps {
  areas: IBuildingArea[];
  buildingId: number | undefined;
  setAreas: (areas: IBuildingArea[]) => void;
}
export const BuildingAreasList = (props: BuildingAreasListProps) => {
  const { areas, setAreas, buildingId } = props;
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [value, setValue] = React.useState('');
  const [deleteAreaIndex, setDeleteAreaIndex] = React.useState<number | null>(null);
  const [deleteArea, { isLoading, isSuccess, isError, error }] = useDeleteBuildingAreaMutation();
  const errorToast = useErrorToast();

  React.useEffect(() => {
    if (isSuccess) {
      const clone = [...areas];
      if (deleteAreaIndex !== null) {
        clone.splice(deleteAreaIndex, 1);
        setAreas(clone);
        setDeleteAreaIndex(null);
      }
      toast.success(<SuccessMsg message="Area removed successfully" />);
    }
    if (isError && error) {
      errorToast.open({
        // @ts-ignore - property data does not exist on type
        message: error.data.payload.message,
        autoClose: false,
        append: (
          <List>
            {/* @ts-ignore - property data does not exist on type */}
            {error.data.payload.zones.map((err: string, i: number) => (
              <ListItem key={i}>#{err}</ListItem>
            ))}
          </List>
        ),
      });
      console.log('isError', error);
    }
  }, [isError, isSuccess]);

  const SuccessMsg = ({ message }: { message: string }) => (
    <Box>
      <Typography variant="body2">{message}</Typography>
    </Box>
  );

  const handleAddNewArea = () => {
    if (value) {
      setAreas(
        [...areas, { building_id: buildingId, name: value }].sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          if (nameA > nameB) {
            return 1;
          }
          if (nameA > nameB) {
            return -1;
          }

          return 0;
        }),
      );
      setValue('');
    }
  };

  const handleDeleteArea = (index: number) => {
    const areaId = areas[index].id;
    if (buildingId && areaId !== undefined) {
      setDeleteAreaIndex(index);
      deleteArea({ orgSlug, id: buildingId, areaId });
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      <TextField
        label="Add Building Areas"
        variant="outlined"
        placeholder="Main Lobby, Suite 410, etc..."
        fullWidth
        onKeyUp={(e) => e.key === 'Enter' && handleAddNewArea()}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleAddNewArea}>
                <ArrowForward />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {areas.length === 1 && <Typography variant="caption">At least 1 area is required</Typography>}
      <Box sx={{ mt: 1 }}>
        {areas.map((a, i) => (
          <Chip
            key={i}
            label={a.name}
            variant="outlined"
            color="primary"
            disabled={areas.length === 1 || isLoading}
            onDelete={() => handleDeleteArea(i)}
            sx={{ mr: 1, mb: 1 }}
          />
        ))}
      </Box>
    </Box>
  );
};
